import { ChartColumnIncreasing, Pause, Play } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Feedback } from "@/lib/messaging";
import { FeedbackAnalysisCard } from "@/components/pages/voicechat/feedback-analysis-card";
import { Slider } from "@/components/ui/slider";
import { TooltipWithIcon } from "@/components/tooltip-with-icon";
import { renderTxt } from "@/lib/utils";
import { useAudioPlayback } from "@/context/audio-playback-context";
import { useTranslation } from "react-i18next";

interface AudioChatMessageProps {
  id: string;
  text: string;
  src?: string;
  play?: boolean;
  onAudioActiveChange: (id: string, isActive: boolean) => void;
  feedback?: Feedback;
}

const playbackRates = [0.75, 1.0, 1.25, 1.5];

export function AudioChatMessageComponent({
  id,
  src,
  text,
  onAudioActiveChange,
  play = false,
  feedback,
}: AudioChatMessageProps) {
  const { t } = useTranslation("common");
  const { registerPlayer, unregisterPlayer } = useAudioPlayback();
  const hasGreenHighlight = text.includes("green-highlight");
  const hasYellowHighlight = text.includes("yellow-highlight");
  const [isPlaying, setIsPlaying] = useState(play);
  const [progress, setProgress] = useState(0);
  const [playbackRateIndex, setPlaybackRateIndex] = useState(1); // Start at 1.0x speed
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      unregisterPlayer(`audio-${id}`);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [id, unregisterPlayer]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    // Keep backward compatibility with existing prop
    onAudioActiveChange(id, isPlaying);

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Register with global audio context
      registerPlayer(`audio-${id}`);

      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        setIsPlaying(false);
        unregisterPlayer(`audio-${id}`);
      });
    } else {
      audio.pause();
      // Unregister when paused
      unregisterPlayer(`audio-${id}`);
    }
  }, [
    audioRef,
    isPlaying,
    id,
    registerPlayer,
    unregisterPlayer,
    onAudioActiveChange,
  ]);

  const handleProgressChange = (newProgress: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = (newProgress[0] / 100) * audio.duration;
    audio.currentTime = newTime;
    setProgress(newProgress[0]);
  };

  const changePlaybackRate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const newIndex = (playbackRateIndex + 1) % playbackRates.length;
    const newRate = playbackRates[newIndex];
    audio.playbackRate = newRate;
    setPlaybackRateIndex(newIndex);
  };

  return (
    <Card className="flex flex-col p-1 bg-card rounded-lg shadow-sm max-w-md lg:max-w-lg xl:max-w-2xl 2xl:max-w-4xl">
      {(hasGreenHighlight || hasYellowHighlight) && (
        <div className="flex gap-2 px-2 pt-1.5 flex-wrap justify-end">
          {hasYellowHighlight && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full yellow-highlight">
              {t("highlights.pronunciation")}
            </span>
          )}
          {hasGreenHighlight && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full green-highlight">
              {t("highlights.suggestion")}
            </span>
          )}
        </div>
      )}
      <div className="flex justify-between space-x-2">
        <p
          className="text-sm pt-2 px-2"
          dangerouslySetInnerHTML={renderTxt(text)}
        />
        {feedback && (
          <div className="relative p-4">
            <TooltipWithIcon
              className="absolute top-2 right-3 text-ternary"
              tooltipClassName="max-w-sm lg:md:max-w-lg min-w-40"
              content={
                <FeedbackAnalysisCard
                  feedbackData={feedback}
                  size="sm"
                  inline
                />
              }
              icon={ChartColumnIncreasing}
              iconProps={{ size: 18 }}
              side="left"
            />
          </div>
        )}
      </div>
      {/* Empty tags with dynamic styles so that tailwind create the classes on advance */}
      <p className="hidden bg-green-400 dark:bg-green-800" />
      <p className="hidden bg-yellow-400 dark:bg-yellow-700" />
      {src && (
        <div className="flex items-center space-x-2 p-2 max-w-72">
          <div>
            <Button
              size="icon"
              variant="ghost"
              onClick={togglePlayPause}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="rounded-lg h-4 w-4" />
              ) : (
                <Play className="rounded-lg h-4 w-4" />
              )}
            </Button>
          </div>
          <Slider
            value={[progress]}
            max={100}
            step={1}
            onValueChange={handleProgressChange}
            className="flex-grow"
            aria-label="Audio progress"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={changePlaybackRate}
            aria-label={`Change playback speed, current speed ${playbackRates[playbackRateIndex]}x`}
            className="rounded-full w-14"
          >
            {playbackRates[playbackRateIndex]}x
          </Button>
        </div>
      )}
      <audio crossOrigin="anonymous" ref={audioRef} src={src} />
    </Card>
  );
}
